import React, { useState } from 'react';
import './SignalementIncident.css';
import signalementService from '../services/signalementService';

function SignalementIncident({ onClose, onSuccess }) {
  const [step, setStep] = useState(1);
  const [formData, setFormData] = useState({
    type_incident: '',
    description: '',
    lieu_incident: '',
    date_incident: '',
    heure_incident: '',
    personne_impliquee: '',
    temoin_present: false,
    details_temoin: '',
    action_souhaitee: '',
    anonyme: false,
    nom_declarant: '',
    prenom_declarant: '',
    relation_victime: '',
    telephone_contact: '',
    email_contact: '',
    fichiers: []
  });
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({});

  const typesIncidents = {
    'harcelement': 'Harcèlement',
    'agression_sexuelle': 'Agression sexuelle',
    'violence_physique': 'Violence physique',
    'discrimination': 'Discrimination',
    'intimidation': 'Intimidation',
    'cyberharclement': 'Cyberharcèlement',
    'autre': 'Autre'
  };

  const relationsVictime = {
    'victime': 'Je suis la victime',
    'parent': 'Je suis le parent/tuteur de la victime',
    'temoin': 'J\'ai été témoin de l\'incident',
    'proche': 'Je suis un proche de la victime',
    'autre': 'Autre'
  };

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
    
    // Effacer l'erreur si elle existe
    if (errors[field]) {
      setErrors(prev => ({
        ...prev,
        [field]: null
      }));
    }
  };

  const handleFileChange = (e) => {
    const files = Array.from(e.target.files);
    if (files.length > 5) {
      alert('Vous ne pouvez joindre que 5 fichiers maximum');
      return;
    }
    
    // Vérifier la taille des fichiers (5MB max chacun)
    const oversizedFiles = files.filter(file => file.size > 5 * 1024 * 1024);
    if (oversizedFiles.length > 0) {
      alert('Certains fichiers dépassent la taille limite de 5MB');
      return;
    }

    setFormData(prev => ({
      ...prev,
      fichiers: files
    }));
  };

  const validateStep = (stepNumber) => {
    const newErrors = {};

    if (stepNumber === 1) {
      if (!formData.type_incident) {
        newErrors.type_incident = 'Veuillez sélectionner le type d\'incident';
      }
      if (!formData.description || formData.description.length < 10) {
        newErrors.description = 'Veuillez décrire l\'incident (minimum 10 caractères)';
      }
    }

    if (stepNumber === 2) {
      if (!formData.anonyme) {
        if (!formData.nom_declarant) {
          newErrors.nom_declarant = 'Nom requis pour un signalement non anonyme';
        }
        if (!formData.prenom_declarant) {
          newErrors.prenom_declarant = 'Prénom requis pour un signalement non anonyme';
        }
        if (!formData.relation_victime) {
          newErrors.relation_victime = 'Veuillez préciser votre relation avec la victime';
        }
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const nextStep = () => {
    if (validateStep(step)) {
      setStep(step + 1);
    }
  };

  const prevStep = () => {
    setStep(step - 1);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateStep(step)) {
      return;
    }

    setLoading(true);
    
    try {
      const formDataToSend = new FormData();
      
      // Ajouter tous les champs
      Object.keys(formData).forEach(key => {
        if (key === 'fichiers') {
          formData.fichiers.forEach((file, index) => {
            formDataToSend.append(`fichiers[${index}]`, file);
          });
        } else if (key === 'temoin_present' || key === 'anonyme') {
          formDataToSend.append(key, formData[key] ? '1' : '0');
        } else if (formData[key] !== '') {
          formDataToSend.append(key, formData[key]);
        }
      });

      const response = await signalementService.create(formDataToSend);
      
      if (onSuccess) {
        onSuccess(response.data);
      }
      
      alert(`Signalement enregistré avec succès.\nNuméro de référence: ${response.data.numero_signalement}`);
      onClose();
      
    } catch (error) {
      console.error('Erreur:', error);
      if (error.response?.data?.errors) {
        setErrors(error.response.data.errors);
      } else {
        alert('Erreur lors de l\'enregistrement du signalement. Veuillez réessayer.');
      }
    } finally {
      setLoading(false);
    }
  };

  const renderStep1 = () => (
    <div className="signalement-step">
      <h3>Informations sur l'incident</h3>
      
      <div className="form-group">
        <label>Type d'incident *</label>
        <select 
          value={formData.type_incident} 
          onChange={(e) => handleInputChange('type_incident', e.target.value)}
          className={errors.type_incident ? 'error' : ''}
        >
          <option value="">Sélectionnez le type d'incident</option>
          {Object.entries(typesIncidents).map(([key, label]) => (
            <option key={key} value={key}>{label}</option>
          ))}
        </select>
        {errors.type_incident && <span className="error-message">{errors.type_incident}</span>}
      </div>

      <div className="form-group">
        <label>Description détaillée de l'incident *</label>
        <textarea 
          value={formData.description}
          onChange={(e) => handleInputChange('description', e.target.value)}
          placeholder="Décrivez ce qui s'est passé de manière détaillée..."
          rows="5"
          className={errors.description ? 'error' : ''}
        />
        <small>Minimum 10 caractères - {formData.description.length}/500</small>
        {errors.description && <span className="error-message">{errors.description}</span>}
      </div>

      <div className="form-row">
        <div className="form-group">
          <label>Lieu de l'incident</label>
          <input 
            type="text"
            value={formData.lieu_incident}
            onChange={(e) => handleInputChange('lieu_incident', e.target.value)}
            placeholder="Ex: Salle de classe, cour de récréation..."
          />
        </div>
        <div className="form-group">
          <label>Date de l'incident</label>
          <input 
            type="date"
            value={formData.date_incident}
            onChange={(e) => handleInputChange('date_incident', e.target.value)}
            max={new Date().toISOString().split('T')[0]}
          />
        </div>
      </div>

      <div className="form-row">
        <div className="form-group">
          <label>Heure approximative</label>
          <input 
            type="time"
            value={formData.heure_incident}
            onChange={(e) => handleInputChange('heure_incident', e.target.value)}
          />
        </div>
        <div className="form-group">
          <label>Personne(s) impliquée(s)</label>
          <input 
            type="text"
            value={formData.personne_impliquee}
            onChange={(e) => handleInputChange('personne_impliquee', e.target.value)}
            placeholder="Nom ou description de la personne"
          />
        </div>
      </div>

      <div className="form-group">
        <label className="checkbox-label">
          <input 
            type="checkbox"
            checked={formData.temoin_present}
            onChange={(e) => handleInputChange('temoin_present', e.target.checked)}
          />
          Il y avait des témoins
        </label>
      </div>

      {formData.temoin_present && (
        <div className="form-group">
          <label>Détails sur les témoins</label>
          <textarea 
            value={formData.details_temoin}
            onChange={(e) => handleInputChange('details_temoin', e.target.value)}
            placeholder="Noms des témoins ou description..."
            rows="3"
          />
        </div>
      )}
    </div>
  );

  const renderStep2 = () => (
    <div className="signalement-step">
      <h3>Vos informations</h3>
      
      <div className="form-group">
        <label className="checkbox-label">
          <input 
            type="checkbox"
            checked={formData.anonyme}
            onChange={(e) => handleInputChange('anonyme', e.target.checked)}
          />
          Je souhaite rester anonyme
        </label>
        <small>Si vous choisissez l'anonymat, nous ne pourrons pas vous recontacter pour des précisions.</small>
      </div>

      {!formData.anonyme && (
        <>
          <div className="form-row">
            <div className="form-group">
              <label>Nom *</label>
              <input 
                type="text"
                value={formData.nom_declarant}
                onChange={(e) => handleInputChange('nom_declarant', e.target.value)}
                className={errors.nom_declarant ? 'error' : ''}
              />
              {errors.nom_declarant && <span className="error-message">{errors.nom_declarant}</span>}
            </div>
            <div className="form-group">
              <label>Prénom *</label>
              <input 
                type="text"
                value={formData.prenom_declarant}
                onChange={(e) => handleInputChange('prenom_declarant', e.target.value)}
                className={errors.prenom_declarant ? 'error' : ''}
              />
              {errors.prenom_declarant && <span className="error-message">{errors.prenom_declarant}</span>}
            </div>
          </div>

          <div className="form-group">
            <label>Votre relation avec la victime *</label>
            <select 
              value={formData.relation_victime}
              onChange={(e) => handleInputChange('relation_victime', e.target.value)}
              className={errors.relation_victime ? 'error' : ''}
            >
              <option value="">Sélectionnez votre relation</option>
              {Object.entries(relationsVictime).map(([key, label]) => (
                <option key={key} value={key}>{label}</option>
              ))}
            </select>
            {errors.relation_victime && <span className="error-message">{errors.relation_victime}</span>}
          </div>

          <div className="form-row">
            <div className="form-group">
              <label>Téléphone de contact</label>
              <input 
                type="tel"
                value={formData.telephone_contact}
                onChange={(e) => handleInputChange('telephone_contact', e.target.value)}
                placeholder="Ex: +229 XX XX XX XX"
              />
            </div>
            <div className="form-group">
              <label>Email de contact</label>
              <input 
                type="email"
                value={formData.email_contact}
                onChange={(e) => handleInputChange('email_contact', e.target.value)}
                placeholder="votre.email@example.com"
              />
            </div>
          </div>
        </>
      )}
    </div>
  );

  const renderStep3 = () => (
    <div className="signalement-step">
      <h3>Finalisation</h3>
      
      <div className="form-group">
        <label>Action souhaitée</label>
        <textarea 
          value={formData.action_souhaitee}
          onChange={(e) => handleInputChange('action_souhaitee', e.target.value)}
          placeholder="Que souhaitez-vous que l'école fasse suite à ce signalement ?"
          rows="4"
        />
      </div>

      <div className="form-group">
        <label>Fichiers joints (optionnel)</label>
        <input 
          type="file"
          multiple
          accept=".pdf,.doc,.docx,.jpg,.jpeg,.png"
          onChange={handleFileChange}
        />
        <small>
          Formats acceptés: PDF, DOC, DOCX, JPG, PNG<br/>
          Taille max: 5MB par fichier, 5 fichiers maximum
        </small>
        {formData.fichiers.length > 0 && (
          <div className="files-list">
            {Array.from(formData.fichiers).map((file, index) => (
              <div key={index} className="file-item">
                📎 {file.name} ({(file.size / 1024 / 1024).toFixed(2)} MB)
              </div>
            ))}
          </div>
        )}
      </div>

      <div className="warning-box">
        <h4>⚠️ Important</h4>
        <ul>
          <li>Ce signalement sera traité de manière confidentielle</li>
          <li>L'équipe administrative prendra contact si nécessaire</li>
          <li>Les signalements critiques sont traités en priorité</li>
          <li>Vous recevrez un numéro de référence pour le suivi</li>
        </ul>
      </div>
    </div>
  );

  return (
    <div className="signalement-modal-overlay">
      <div className="signalement-modal">
        <div className="signalement-header">
          <h2>🛡️ Signalement d'incident</h2>
          <button className="close-btn" onClick={onClose}>×</button>
        </div>

        <div className="signalement-progress">
          <div className={`progress-step ${step >= 1 ? 'active' : ''}`}>1</div>
          <div className={`progress-line ${step >= 2 ? 'active' : ''}`}></div>
          <div className={`progress-step ${step >= 2 ? 'active' : ''}`}>2</div>
          <div className={`progress-line ${step >= 3 ? 'active' : ''}`}></div>
          <div className={`progress-step ${step >= 3 ? 'active' : ''}`}>3</div>
        </div>

        <form onSubmit={handleSubmit} className="signalement-form">
          {step === 1 && renderStep1()}
          {step === 2 && renderStep2()}
          {step === 3 && renderStep3()}

          <div className="signalement-actions">
            {step > 1 && (
              <button type="button" className="btn-secondary" onClick={prevStep}>
                Précédent
              </button>
            )}
            
            {step < 3 ? (
              <button type="button" className="btn-primary" onClick={nextStep}>
                Suivant
              </button>
            ) : (
              <button type="submit" className="btn-primary" disabled={loading}>
                {loading ? 'Envoi en cours...' : 'Envoyer le signalement'}
              </button>
            )}
          </div>
        </form>
      </div>
    </div>
  );
}

export default SignalementIncident;